import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Package, Users, Bell, TrendingUp, Activity, Heart,
  ArrowUpRight, ArrowDownRight, AlertTriangle, Settings2,
  GripVertical, Eye, EyeOff, RefreshCw, Loader2
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const { t, language } = useI18n();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [repricing, setRepricing] = useState(null);
  const [widgets, setWidgets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCustomize, setShowCustomize] = useState(false);
  const [pendingWidgets, setPendingWidgets] = useState([]);

  const fetchAll = useCallback(() => {
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/dashboard/alerts"),
      api.get("/dashboard/top-products"),
      api.get("/dashboard/preferences"),
      api.get("/repricing/stats").catch(() => ({ data: null })),
    ]).then(([s, a, tp, prefs, rp]) => {
      setStats(s.data);
      setAlerts(a.data);
      setTopProducts(tp.data);
      setWidgets(prefs.data.widgets);
      setRepricing(rp.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const isVisible = (id) => {
    if (!widgets) return true;
    const w = widgets.find((w) => w.id === id);
    return w ? w.visible : true;
  };

  const openCustomize = () => {
    setPendingWidgets(widgets ? [...widgets] : []);
    setShowCustomize(true);
  };

  const toggleWidget = (id) => {
    setPendingWidgets((prev) => prev.map((w) => w.id === id ? { ...w, visible: !w.visible } : w));
  };

  const moveWidget = (idx, dir) => {
    setPendingWidgets((prev) => {
      const arr = [...prev];
      const swapIdx = idx + dir;
      if (swapIdx < 0 || swapIdx >= arr.length) return arr;
      [arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]];
      return arr.map((w, i) => ({ ...w, order: i }));
    });
  };

  const savePrefs = async () => {
    try {
      const res = await api.put("/dashboard/preferences", { widgets: pendingWidgets });
      setWidgets(res.data.widgets);
      setShowCustomize(false);
      toast.success(t("dashboard.saved"));
    } catch { toast.error(t("toast.error")); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  const healthScore = stats?.health_score || 0;
  const healthColor = healthScore >= 70 ? "text-emerald-500" : healthScore >= 40 ? "text-amber-500" : "text-red-500";
  const pieData = [
    { name: t("dashboard.winning"), value: stats?.products_cheapest || 0 },
    { name: t("dashboard.below"), value: stats?.products_competitor_cheaper || 0 },
    { name: t("dashboard.other"), value: stats?.products_no_competitor || 0 },
  ];
  const COLORS = ["#10B981", "#EF4444", "#6B7280"];

  const sortedWidgets = widgets ? [...widgets].sort((a, b) => a.order - b.order) : [];

  const widgetComponents = {
    stat_cards: () => (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: t("dashboard.active_products"), value: stats?.total_products || 0, icon: Package, color: "text-blue-500" },
          { label: t("dashboard.total_competitors"), value: stats?.total_competitors || 0, icon: Users, color: "text-emerald-500" },
          { label: t("dashboard.active_alerts"), value: stats?.active_alerts || 0, icon: Bell, color: "text-amber-500" },
          { label: t("dashboard.extra_profit"), value: `${stats?.extra_profit_potential || 0} EUR`, icon: TrendingUp, color: "text-emerald-500", mono: true },
        ].map((s, i) => (
          <Card key={i} className="bg-card border-border hover:border-primary/30 transition-colors" data-testid={`stat-card-${i}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-muted-foreground">{s.label}</span>
                <s.icon className={`w-4 h-4 ${s.color}`} />
              </div>
              <p className={`text-2xl font-bold ${s.mono ? "font-mono-data" : ""}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    ),
    health_score: () => (
      <Card className="bg-card border-border" data-testid="health-score-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2"><Heart className="w-4 h-4" /> {t("dashboard.health_score")}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center py-6">
          <div className="relative w-32 h-32">
            <svg className="health-ring w-32 h-32" viewBox="0 0 128 128">
              <circle cx="64" cy="64" r="56" fill="none" stroke="hsl(var(--border))" strokeWidth="8" />
              <circle cx="64" cy="64" r="56" fill="none" stroke={healthScore >= 70 ? "#10B981" : healthScore >= 40 ? "#F59E0B" : "#EF4444"} strokeWidth="8" strokeDasharray={`${healthScore * 3.52} 352`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-3xl font-bold font-mono-data ${healthColor}`}>{healthScore}</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-4">{t("dashboard.health_desc")}</p>
        </CardContent>
      </Card>
    ),
    opportunity_radar: () => (
      <Card className="bg-card border-border" data-testid="opportunity-radar-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2"><Package className="w-4 h-4" /> {t("dashboard.competition_title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg">
              <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-emerald-500" /><span className="text-sm">{t("dashboard.cheapest")}</span></div>
              <span className="font-bold font-mono-data text-emerald-500">{pieData[0].value}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-red-500/10 rounded-lg">
              <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-red-500" /><span className="text-sm">{t("dashboard.competitor_cheaper")}</span></div>
              <span className="font-bold font-mono-data text-red-500">{pieData[1].value}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-accent/30 rounded-lg">
              <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-muted-foreground" /><span className="text-sm">{t("dashboard.no_competitor")}</span></div>
              <span className="font-bold font-mono-data text-muted-foreground">{pieData[2].value}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    ),
    recent_alerts: () => (
      <Card className="bg-card border-border" data-testid="recent-alerts-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2"><Bell className="w-4 h-4" /> {t("dashboard.recent_alerts")}</CardTitle>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">{t("alerts.no_alerts")}</p> : (
            <div className="space-y-3">
              {alerts.slice(0, 5).map((alert) => (
                <div key={alert.id} className="flex items-start gap-3 p-2 rounded-md hover:bg-accent/50 transition-colors">
                  {alert.type === "price_change" ? <ArrowDownRight className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{alert.message}</p>
                    <Badge variant="secondary" className="text-xs mt-1">{alert.type === "price_change" ? t("alerts.price_change") : t("alerts.below_margin")}</Badge>
                  </div>
                  {!alert.read && <div className="status-dot active mt-1.5" />}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    ),
    top_products: () => {
      // "{t("dashboard.action_needed")}" - products where competitors are cheaper or margin is low
      const actionNeeded = topProducts.filter(p => p.margin_pct < 15).slice(0, 5);
      const cheaperComps = topProducts.filter(p => p.margin_pct >= 15).slice(0, 5);
      return (
        <Card className="bg-card border-border" data-testid="top-products-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> {t("dashboard.action_needed")}</CardTitle></CardHeader>
          <CardContent>
            {actionNeeded.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-emerald-500 font-medium text-sm">{t("dashboard.all_healthy")}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("dashboard.no_action")}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {actionNeeded.map((p) => (
                  <div key={p.id} className="flex items-center justify-between p-2.5 rounded-lg bg-accent/30 hover:bg-accent/50 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{p.name}</p>
                      <p className="text-xs text-muted-foreground font-mono-data">{p.price?.toFixed(2)} EUR</p>
                    </div>
                    <Badge className={`font-mono-data border-0 ml-2 ${p.margin_pct < 0 ? "bg-red-500/20 text-red-400" : p.margin_pct < 10 ? "bg-amber-500/20 text-amber-400" : "bg-blue-500/20 text-blue-400"}`}>{p.margin_pct}%</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      );
    },
    repricing_summary: () => repricing ? (
      <Card className="bg-card border-border" data-testid="repricing-summary-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><TrendingUp className="w-4 h-4" /> {t("dashboard.repricing_title")}</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: t("dashboard.repricing_pending"), val: repricing.pending || 0, color: "text-amber-500" },
              { label: t("dashboard.repricing_accepted"), val: repricing.accepted || 0, color: "text-emerald-500" },
              { label: t("dashboard.repricing_rejected"), val: repricing.rejected || 0, color: "text-red-500" },
              { label: t("dashboard.repricing_impact"), val: `${repricing.total_profit_impact || 0} EUR`, color: "text-blue-500", mono: true },
            ].map((s, i) => (
              <div key={i} className="text-center p-3 bg-accent/30 rounded-lg">
                <p className="text-xs text-muted-foreground">{s.label}</p>
                <p className={`text-lg font-bold ${s.color} ${s.mono ? "font-mono-data" : ""}`}>{s.val}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    ) : null,
    monitoring_status: () => (
      <Card className="bg-card border-border" data-testid="monitoring-status-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-2"><Activity className="w-4 h-4" /> {t("dashboard.monitoring_status")}</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="status-dot active" />
            <div>
              <p className="text-sm font-medium">{t("dashboard.monitoring_active")}</p>
              <p className="text-xs text-muted-foreground">
                {stats?.last_monitoring_run?.status === "running"
                  ? `${{ nl: "Scan bezig", en: "Scan in progress", fr: "Analyse en cours" }[language] || "Scan bezig"}: ${(stats.last_monitoring_run.products_scanned || 0) + (stats.last_monitoring_run.products_failed || 0)} / ${stats.last_monitoring_run.products_total ?? "?"}`
                  : <>{t("dashboard.last_run")}: {stats?.last_monitoring_run ? new Date(stats.last_monitoring_run.started_at).toLocaleString("nl-NL") : t("dashboard.not_started")}</>}
              </p>
            </div>
          </div>
          {stats?.last_monitoring_run && (
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="text-center p-2 bg-accent/30 rounded-lg">
                <p className="text-xs text-muted-foreground">{t("dashboard.scanned")}</p>
                <p className="text-lg font-bold font-mono-data">{stats.last_monitoring_run.products_scanned}</p>
              </div>
              <div className="text-center p-2 bg-accent/30 rounded-lg">
                <p className="text-xs text-muted-foreground">{t("dashboard.found")}</p>
                <p className="text-lg font-bold font-mono-data">{stats.last_monitoring_run.competitors_found}</p>
              </div>
              <div className="text-center p-2 bg-accent/30 rounded-lg">
                <p className="text-xs text-muted-foreground">{t("dashboard.changes")}</p>
                <p className="text-lg font-bold font-mono-data">{stats.last_monitoring_run.price_changes}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    ),
  };

  // Layout: stat_cards = full width, then groups of widgets
  const renderWidgets = () => {
    const visible = sortedWidgets.filter((w) => w.visible);
    const fullWidthIds = new Set(["stat_cards", "top_products", "repricing_summary", "monitoring_status"]);
    const items = [];

    for (const w of visible) {
      const Comp = widgetComponents[w.id];
      if (!Comp) continue;
      const rendered = Comp();
      if (!rendered) continue;
      items.push({ id: w.id, el: rendered, fullWidth: fullWidthIds.has(w.id) });
    }

    const result = [];
    let row = [];
    const flushRow = () => {
      if (row.length > 0) {
        const cols = row.length === 1 ? "" : row.length === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3";
        result.push(<div key={`row-${row[0].id}`} className={`grid grid-cols-1 ${cols} gap-6`}>{row.map((r) => <div key={r.id}>{r.el}</div>)}</div>);
        row = [];
      }
    };

    for (const item of items) {
      if (item.fullWidth) {
        flushRow();
        result.push(<div key={item.id}>{item.el}</div>);
      } else {
        row.push(item);
        if (row.length === 3) flushRow();
      }
    }
    flushRow();
    return result;
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("dashboard.title")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t("dashboard.welcome")}, {user?.name}</p>
        </div>
        <Dialog open={showCustomize} onOpenChange={setShowCustomize}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" onClick={openCustomize} data-testid="customize-dashboard-btn">
              <Settings2 className="w-4 h-4 mr-2" /> {t("dashboard.customize")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{t("dashboard.customize_title")}</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground mb-4">{t("dashboard.customize_desc")}</p>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {pendingWidgets.sort((a, b) => a.order - b.order).map((w, idx) => (
                <div key={w.id} className="flex items-center gap-3 p-3 bg-accent/30 rounded-lg" data-testid={`widget-config-${w.id}`}>
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => moveWidget(idx, -1)} disabled={idx === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors"><GripVertical className="w-3.5 h-3.5 rotate-180" /></button>
                    <button onClick={() => moveWidget(idx, 1)} disabled={idx === pendingWidgets.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-20 transition-colors"><GripVertical className="w-3.5 h-3.5" /></button>
                  </div>
                  <span className="flex-1 text-sm font-medium">{w.label}</span>
                  <Switch checked={w.visible} onCheckedChange={() => toggleWidget(w.id)} data-testid={`toggle-widget-${w.id}`} />
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCustomize(false)}>{t("common.cancel")}</Button>
              <Button onClick={savePrefs} className="bg-blue-600 hover:bg-blue-700" data-testid="save-dashboard-prefs-btn">{t("common.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {renderWidgets()}
    </div>
  );
}
